import {Component, computed, inject, model, ModelSignal} from '@angular/core';
import {DefaultFormApiAdapter, IAction, IForm, TAnyFormElement} from 'frontier/browserkit';
import AppControlGuids from '../../../../api/ControlGuids';
import {ApiAdapter, EsvgFiles} from 'frontier';
import {
  JSON_EXPORT_ACTION,
  JSON_IMPORT_ACTION
} from 'frontier/browserkit/src/lib/components/control/table-control/default-form-actions.constant';
import {
  CustomDynamicFormComponent
} from 'frontier/browserkit/src/lib/components/control/form-control/dynamic-form/custom-dynamic-form/custom-dynamic-form.component';
import {MatDivider} from '@angular/material/divider';
import {
  FormElementComponent
} from 'frontier/browserkit/src/lib/components/control/form-control/dynamic-form/form-element/form-element.component';
import {
  DynamicFormActionComponent
} from 'frontier/browserkit/src/lib/components/control/form-control/dynamic-form/dynamic-form-action/dynamic-form-action.component';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {UiHelperService} from 'frontier/browserkit/src/lib/components/ui-helper/ui-helper.service';
import {InvoiceInfoHelpComponent} from './invoice-info-help/invoice-info-help.component';
import {HelpIconComponent} from 'frontier/browserkit/src/lib/components/help-icon/help-icon.component';
import {TenantHelpComponent} from './tenant-help/tenant-help.component';
import {DebitorAccountHelpComponent} from './debitor-account-help/debitor-account-help.component';
import {AccountingAggregationHelpComponent} from './accounting-aggregation-help/accounting-aggregation-help.component';
import {InterfaceConfigHelpComponent} from './interface-config-help/interface-config-help.component';
import {ExportOptionsHelpComponent} from './export-options-help/export-options-help.component';

class BookingOptionsApiAdapter extends DefaultFormApiAdapter {
  override from(apiData: IForm): IForm {
    console.log(apiData);
    const mappedApiData = {
      ...apiData,
      formGroups: [
        {
          ...apiData.formGroups?.[0],
          elements: apiData.formGroups?.[0]?.elements?.slice(0, 3) ?? [],
        },
        {
          ...apiData.formGroups?.[0],
          elements: apiData.formGroups?.[0]?.elements?.slice(3) ?? [],
          header: 'yy    = \'\\yy\';   // ShortYear, Leistungsjahr 2-stellig\n' +
            '      yyyy  = \'\\yyyy\'; // Year, Leistungsjahr 4-stellig\n' +
            '      mm    = \'\\mm\';   // Month, Leistungsmonat\n' +
            '      dd    = \'\\dd\';   // Day, Leistungstag\n' +
            '      pcn   = \'\\pcn\';  // payer collective number, Sammelnummer\n' +
            '      plz   = \'\\plz\';  // PLZ, vom Transport\n' +
            '      tn    = \'\\tn\';   // tenant number\n' +
            '      vn    = \'\\vn\';   // Version number\n' +
            '      cn    = \'\\cn\';   // cart number\n' +
            '      space = \'\\w\';    // white space\n' +
            '      sn    = \'\\sn\';   // sequential number',
        },
      ],
    };
    return super.from(mappedApiData);
  }
}

enum EFormKeys {
  debitorAccountDiverse = 'tbookingoptions;0',
  debitorAccountUnknown = 'tbookingoptions;1',
  tenantNumber = 'tbookingoptions;2',
  invoiceNumber = 'tbookingoptions;3',
  collectiveInvoiceNumber = 'tbookingoptions;4',
  cancelRequestMode = 'tbookingoptions;5',
  creditAggregation = 'tbookingoptions;6',
  debitAggregation = 'tbookingoptions;7',
  diamondDocumentType = 'tbookingoptions;8',
  extfConsultantNumber = 'tbookingoptions;9',
  extfGlAccountLength = 'tbookingoptions;10',
  exportJournal = 'tbookingoptions;11',
  lastAnnualFinancialStatements = 'tbookingoptions;12',
  votingCircle = 'tbookingoptions;13',
  constCenter = 'tbookingoptions;14',
  diamondDocumentTypeCreditMemo = 'tbookingoptions;15',
  monthlyFileExport = 'tbookingoptions;16',
  diamondExtendedFDataset = 'tbookingoptions;17',
  diamondInvoiceDateAsReceiptDate = 'tbookingoptions;18',
  diamondSDatasetExport = 'tbookingoptions;19',
  swapName = 'tbookingoptions;20',
  debitorSepa = 'tbookingoptions;21',
  addColumnDescriptions = 'tbookingoptions;22',
  dunningType = 'tbookingoptions;23',
  paymentTerm = 'tbookingoptions;24',
  noInvoiceNumberInTransactionText = 'tbookingoptions;26',
  invoiceExport = 'tbookingoptions;27',
}

@Component({
  selector: 'app-booking-options-config-form',
  templateUrl: './booking-options-config-form.component.html',
  styleUrl: './booking-options-config-form.component.scss',
  standalone: true,
  imports: [
    MatDivider,
    FormElementComponent,
    DynamicFormActionComponent,
    MatExpansionModule,
    MatTabGroup,
    MatTab,
    HelpIconComponent,
  ],
})
export class BookingOptionsConfigFormComponent extends CustomDynamicFormComponent {
  protected readonly EsvgFiles: typeof EsvgFiles = EsvgFiles;
  EFormKeys: typeof EFormKeys = EFormKeys;

  private _uiHelperService: UiHelperService = inject(UiHelperService);

  override GUID: string = AppControlGuids.TBookingOptionsConfig;
  override apiAdapter: ApiAdapter = new BookingOptionsApiAdapter();
  override actions: ModelSignal<IAction[]> = model<IAction[]>([
    JSON_EXPORT_ACTION,
    JSON_IMPORT_ACTION,
  ]);

  formData = {
    debitorAccountDiverse: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.debitorAccountDiverse)),
    debitorAccountUnknown: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.debitorAccountUnknown)),
    tenantNumber: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.tenantNumber)),
    invoiceNumber: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.invoiceNumber)),
    collectiveInvoiceNumber: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.collectiveInvoiceNumber)),
    cancelRequestMode: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.cancelRequestMode)),
    creditAggregation: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.creditAggregation)),
    debitAggregation: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.debitAggregation)),
    diamondDocumentType: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.diamondDocumentType)),
    extfConsultantNumber: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.extfConsultantNumber)),
    extfGlAccountLength: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.extfGlAccountLength)),
    exportJournal: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.exportJournal)),
    lastAnnualFinancialStatements: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.lastAnnualFinancialStatements)),
    votingCircle: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.votingCircle)),
    constCenter: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.constCenter)),
    diamondDocumentTypeCreditMemo: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.diamondDocumentTypeCreditMemo)),
    diamondExtendedFDataset: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.diamondExtendedFDataset)),
    diamondInvoiceDateAsReceiptDate: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.diamondInvoiceDateAsReceiptDate)),
    diamondSDatasetExport: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.diamondSDatasetExport)),
    monthlyFileExport: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.monthlyFileExport)),
    swapName: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.swapName)),
    debitorSepa: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.debitorSepa)),
    addColumnDescriptions: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.addColumnDescriptions)),
    dunningType: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.dunningType)),
    paymentTerm: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.paymentTerm)),
    noInvoiceNumberInTransactionText: computed((): TAnyFormElement =>
      this.getFormElementByKey(EFormKeys.noInvoiceNumberInTransactionText)),
    invoiceExport: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.invoiceExport)),
  };

  showHelpForInvoiceInfo(): void {
    this._uiHelperService.openHelp({component: InvoiceInfoHelpComponent})
  }

  showHelpForTenant(): void {
    this._uiHelperService.openHelp({component: TenantHelpComponent})
  }

  showHelpForDebitor(): void {
    this._uiHelperService.openHelp({component: DebitorAccountHelpComponent})
  }

  showHelpForAccountingAggregation(): void {
    this._uiHelperService.openHelp({component: AccountingAggregationHelpComponent})
  }

  showHelpForInterfaceConfig(): void {
    this._uiHelperService.openHelp({component: InterfaceConfigHelpComponent})
  }

  showHelpForExportOptions(): void {
    this._uiHelperService.openHelp({component: ExportOptionsHelpComponent})
  }
}
